import { useState } from "react";
import { Check, Copy, ShieldAlert } from "lucide-react";

/**
 * One-time secret reveal modal. Renders the plaintext secret with a copy button
 * and forces the user to acknowledge they've stored it before dismissing.
 */
export default function OneTimeRevealModal({ open, title, secret, hint, onClose }) {
  const [copied, setCopied] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);

  if (!open) return null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* ignore */
    }
  };

  return (
    <div
      data-testid="one-time-reveal-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm px-4"
    >
      <div className="w-full max-w-lg rounded-[12px] bg-white shadow-2xl border border-slate-200">
        <div className="p-6 border-b border-slate-100">
          <div className="flex items-start gap-3">
            <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-amber-50 text-amber-700">
              <ShieldAlert className="h-5 w-5" />
            </span>
            <div>
              <h3 className="font-display text-[19px] text-[#0A1128]">{title}</h3>
              <p className="mt-1 text-[13px] text-slate-600">
                {hint || "This secret is shown only once. Copy it now and store it in a secure location — we can't show it again."}
              </p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-4">
          <div className="rounded-[10px] border border-slate-200 bg-slate-50 px-3.5 py-3 font-mono text-[12.5px] text-[#0A1128] break-all" data-testid="one-time-secret-value">
            {secret}
          </div>
          <button
            onClick={copy}
            data-testid="one-time-copy-btn"
            className="inline-flex items-center gap-2 rounded-full bg-[#1A4232] text-[#F5F1EB] text-[13px] font-medium px-4 py-2 hover:bg-[#0E2A20] transition-colors"
          >
            {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
            {copied ? "Copied to clipboard" : "Copy to clipboard"}
          </button>

          <label className="flex items-start gap-2.5 text-[13px] text-slate-700 select-none">
            <input
              type="checkbox"
              data-testid="one-time-ack-checkbox"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-slate-300 text-[#1A4232] focus:ring-[#1A4232]"
            />
            I've stored this secret in a safe place.
          </label>
        </div>

        <div className="p-5 border-t border-slate-100 flex justify-end">
          <button
            onClick={onClose}
            disabled={!acknowledged}
            data-testid="one-time-confirm-btn"
            className="rounded-full bg-[#0A1128] text-white text-[13px] font-medium px-5 py-2 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#1E293B] transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
